import { Calendar } from "primereact/calendar";
import React from "react";
import { createPortal } from "react-dom";
import {
  FormSectionElements,
  FormSections,
  FormSubSections,
} from "src/entities/formTemplateDataType";
import XMLParser from "../../utilities/XMLParser";
import classNames from "classnames";
import FormValidationUtils from "../../utilities/FormValidationUtils";
import { LiveValidationErrMsg } from "../live-validation-errMsg/LiveValidationErrMsg";
import Utils from "../../utilities/Utils";
import {
  getAppId,
  getParentSection,
  getRootComponent,
  setPublicSecurePageVisibility,
} from "../../utilities/ElementUtils";
import { signinSSO } from "../../security/PublicSecureSSO";
import { parseInlineStyle } from "../../utilities/HtmlUtils";
import { SCRIPT_ENV } from "../../config/RuntimeConfig";

interface Props {
  field: FormSectionElements;
  editable: boolean;
  title;
  labelControlArray;
  requiredField;
  isEng: boolean;
  callback;
  returnIndex?: number;
  callbackStepIndex?: (index: number, returnIndex?: number) => void;
}

interface State {
  portalTarget: Element | null;
}
export class PublicSecureButtonComponent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { portalTarget: null };
  }

  private updatePortalTarget = () => {
    if (typeof document === "undefined") {
      return;
    }

    const target = document.getElementById("header-actions");
    if (target && this.state.portalTarget !== target) {
      this.setState({ portalTarget: target });
    }
  };

  arrayToString = (_a) => {
    if (Array.isArray(_a)) {
      return _a.join(" ");
    } else {
      return [_a];
    }
  };

  private runElementScripts = (element: FormSectionElements) => {
    if (!element || element.Visible === false) {
      return;
    }

    if (element.Datatype === "Row" && element.subSectionList?.length) {
      element.subSectionList.forEach((subSection) => {
        subSection.ListOfFormSectionElements?.forEach((child) => {
          this.runElementScripts(child);
        });
      });
      return;
    }

    FormValidationUtils.runJavascript(element);
  };

  private runSectionElementScripts = (section?: FormSections) => {
    if (!section) {
      return;
    }

    section.ListOfFormSubSections?.forEach((subSection) => {
      subSection.ListOfFormSectionElements?.forEach((element) => {
        this.runElementScripts(element);
      });
    });
  };

  componentDidMount(): void {
    setPublicSecurePageVisibility(this.props.field, this.props.callback);
    this.updatePortalTarget();
  }

  componentDidUpdate(): void {
    const { portalTarget } = this.state;
    if (!portalTarget || !portalTarget.isConnected) {
      if (portalTarget && !portalTarget.isConnected) {
        this.setState({ portalTarget: null }, () => this.updatePortalTarget());
        return;
      }
      this.updatePortalTarget();
    }
    const currentSection = getParentSection(this.props.field);
    const sectionWithFlags = currentSection as FormSections & {
      __preScriptExecuted?: boolean;
    };

    if (
      !sectionWithFlags?.__preScriptExecuted &&
      currentSection?.PreScript &&
      Utils.isNotNullOrEmpty(currentSection.PreScript)
    ) {
      try {
        sectionWithFlags.__preScriptExecuted = true;
        const customJSFunc = eval(`(${currentSection.PreScript})`);
        customJSFunc(currentSection, SCRIPT_ENV);
      } catch (ex) {
        console.log(ex);
      }
    }
  }

  handleSecureButtonClick = async () => {
    const currentSection = getParentSection(this.props.field);
    const root = getRootComponent(this.props.field);

    const stepperScript =
      this.props.field.StepperScript || currentSection?.StepperScript;

    if (stepperScript && Utils.isNotNullOrEmpty(stepperScript)) {
      try {
        const customJSFunc = eval(`(${stepperScript})`);
        await customJSFunc((index) => {
          let destination = index;
          if (this.props.returnIndex != null) {
            destination = this.props.returnIndex;
            this.props.callbackStepIndex?.(destination);
          } else {
            if (typeof destination === "string") {
              const root = getRootComponent(this.props.field);
              const foundSection = root.ListOfFormSections.find(
                (section) => section.ENUTitle == destination
              );
              const indexNum = root.ListOfFormSections.indexOf(foundSection);
              this.props.callbackStepIndex?.(indexNum);
            } else {
              this.props.callbackStepIndex?.(destination);
            }
          }
        });
      } catch (ex) {
        console.log(ex);
      }
    }

    if (currentSection) {
      this.runSectionElementScripts(currentSection);
    } else {
      FormValidationUtils.runJavascript(this.props.field);
    }

    if (currentSection?.Visible !== false) {
      currentSection.Visible = false;
    }

    this.props.callback(root);

    if (
      currentSection?.PostScript &&
      Utils.isNotNullOrEmpty(currentSection.PostScript)
    ) {
      try {
        const customJSFunc = eval(`(${currentSection.PostScript})`);
        await customJSFunc(currentSection, SCRIPT_ENV);
      } catch (ex) {
        console.log(ex);
      }
    }

    await signinSSO(getAppId(this.props.field), this.props.isEng);
  };

  render() {
    let field = this.props.field;
    let controlArray = [];
    let title = this.props.isEng
      ? this.props.field.ENUTitle
      : this.props.field.FRATitle;

    if (field.InitScript && Utils.isNotNullOrEmpty(field.InitScript)) {
      const customJSFunc = eval(`(${field.InitScript})`);
      try {
        customJSFunc(field, SCRIPT_ENV);
      } catch (ex) {
        console.log(ex);
      }

      if (this.props.editable == false || field.Visible == false) {
        return null;
      }
    }

    const defaultStyle: React.CSSProperties = {
      marginTop: "0px",
      textAlign: "left",
    };
    const style = parseInlineStyle(field.InlineStyle) ?? defaultStyle;

    const ClassName =
      "ontario-button " + this.arrayToString(this.props.field.ClassName);
    const buttonContent = (
      <div
        z-index="0"
        style={style}
        key={"button_" + field.parent.Id + "_" + field.Id}
      >
        <button className={ClassName} onClick={this.handleSecureButtonClick}>
          {title}
        </button>
      </div>
    );

    if (this.state.portalTarget) {
      return createPortal(buttonContent, this.state.portalTarget);
    }

    controlArray.push(buttonContent);
    return controlArray;
  }
}
